import { useQueryClient } from '@tanstack/react-query'
import { Building2, ChevronsUpDown } from 'lucide-react'
import { Dropdown } from '@/components/ui'
import { useSessionStore } from '@/stores'
import { useT } from '@/internationalization/i18n'
import { useMemberships } from '@/features/settings/hooks'

/**
 * Switches the acting tenant for users who belong to more than one workspace
 * (agencies, and our own support staff).
 *
 * Switching clears the entire query cache. Anything less risks rendering one
 * tenant's meetings under another's name while a stale query resolves, which is
 * the single worst bug a multi-tenant UI can have.
 *
 * The list comes from GET /memberships. A user with one workspace still gets
 * the switcher, because it is also how they see which workspace they are in.
 */
export function TenantSwitcher() {
  const t = useT()
  const tenant = useSessionStore((s) => s.tenant)
  const switchTenant = useSessionStore((s) => s.switchTenant)
  const queryClient = useQueryClient()
  const { data } = useMemberships()

  if (!tenant) return null
  // A suspended workspace cannot be acted as, so it is not offered.
  const options = (data ?? [tenant]).filter((t) => t.status !== 'suspended')

  return (
    <Dropdown
      heading={t('tenant.workspace')}
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          className="flex h-9 max-w-48 items-center gap-2 rounded-md border border-border bg-surface px-2.5
            text-base text-fg transition-colors hover:bg-surface-muted"
        >
          <Building2 aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" />
          <span className="truncate">{tenant.name}</span>
          <ChevronsUpDown aria-hidden className="h-3.5 w-3.5 shrink-0 text-fg-subtle" />
        </button>
      )}
      items={options.map((option) => ({
        id: option.id,
        label: option.name,
        selected: option.id === tenant.id,
        onSelect: () => {
          if (option.id === tenant.id) return
          switchTenant(option)
          queryClient.clear()
        },
      }))}
    />
  )
}
